<template>
  <div>
    <!-- PopUp of confirmation deleted:start -->
    <AlertMessage ref="alertMessage" id_alert="deleted-confirmation-alert" class="dark" />
    <!-- PopUp of confirmation deleted:end -->

    <!-- Messages-Confirmations:start -->
    <ConfirmMessages :ids="messages" />
    <!-- Messages-Confirmations:end -->

    <!-- Title-and-Name:start -->
    <div class="intro-y mt-8">
      <h2 class="text-lg font-medium mr-auto">
        {{ !platform.id ? $t(`${viewRoot}.new`) : $t(`${viewRoot}.edit`) + ": " + platform.name }}
      </h2>
    </div>
    <!-- Title-and-Name:end -->

    <!-- Loader:start-->
    <div v-if="initialLoading" class="intro-y w-full h-full flex justify-center items-center">
      <div class="wrapper__loading">
        <LoadingIcon icon="tail-spin" />
      </div>
    </div>
    <!-- Loader:end-->

    <!-- Main content:start -->
    <div v-else class="flex flex-row mt-5 mb-12 w-full gap-4">
      <div class="w-full">
        <!-- Form:start -->
        <div class="intro-y col-span-12 lg:col-span-6">
          <ScienceSourcePlatformsModule v-model="platform" :read-only="false" />
        </div>
        <!-- Form:end -->

        <!-- Buttons:start -->
        <div class="w-full flex justify-end mt-5">
          <button
            v-if="platform.id"
            @click="confirmRemovePlatform"
            type="button"
            class="btn btn-outline-dark w-24 mr-5"
            :class="{ no__action__btn: noActionBtn, ignore__opacity: removeLoading }"
          >
            <LoadingIcon v-if="removeLoading" icon="three-dots" />
            <span v-else>{{ $t("views.delete") }}</span>
          </button>

          <button
            @click="savePlatform"
            type="button"
            class="btn btn-primary w-24"
            :class="{ no__action__btn: noActionBtn, ignore__opacity: saveLoading }"
          >
            <LoadingIcon v-if="saveLoading" icon="three-dots" />
            <span v-else>{{ $t("views.save") }}</span>
          </button>
        </div>
        <!-- Buttons:end -->
      </div>
    </div>
    <!-- Main content:end -->
  </div>
</template>

<script>
import Toastify from "toastify-js";

import AlertMessage from "@/components/alert/Main.vue";
import ConfirmMessages from "@/components/message/ConfirmMessages.vue";
import ScienceSourcePlatformsModule from "@/views/science-source/platforms/Module.vue";

import ScienceSourceService from "@/services/science-source.service";

import { ensureShape, validatePlatform, formatToSend, parseFromApi } from "@/utils/modules/science-source/platforms.js";

export default {
  name: "ScienceSourcePlatformsNew",
  components: {
    ConfirmMessages,
    AlertMessage,
    ScienceSourcePlatformsModule,
  },
  data() {
    return {
      platform: {
        id: null,
        name: "",
        regionOfOrigin: "",
        linkPage: "",
        validations: {
          name: "",
          regionOfOrigin: "",
          linkPage: "",
        },
      },

      viewRoot: "views.science_source_platforms",
      queryUrl: this.$route.query,

      noActionBtn: false,
      saveLoading: false,
      removeLoading: false,
      initialLoading: true,
      messages: {
        savedSuccessId: "saved-confirmation-success",
        updatedSuccessId: "updated-confirmation-success",
        removedSuccessId: "removed-confirmation-success",
      },
    };
  },
  created() {
    this.generateInitialStructure();
    this.getInitialData();
  },
  methods: {
    generateInitialStructure() {
      this.platform = ensureShape(this.platform);
    },

    async getInitialData() {
      await this.getPlatformById();
      this.initialLoading = false;
    },
    async getPlatformById() {
      const id = this.queryUrl.id;
      if (!id) return;
      try {
        const response = await ScienceSourceService.getById(id);
        if (!response || !response.data) return;
        this.platform = parseFromApi(response.data);
      } catch (_) {
        console.log("CODE#404");
      }
    },

    savePlatform() {
      this.saveLoading = true;
      this.noActionBtn = true;

      const isValid = validatePlatform(this.platform, {
        t: this.$t,
      });

      if (!isValid) {
        this.saveLoading = false;
        this.noActionBtn = false;
        return;
      }

      const [payload, editMode] = formatToSend(this.platform);
      const finallyBlock = () => {
        this.saveLoading = false;
        this.noActionBtn = false;
      };

      const req = editMode ? ScienceSourceService.update(payload.id, payload) : ScienceSourceService.create(payload);

      req
        .then(() => {
          this.showToast(editMode ? this.messages.updatedSuccessId : this.messages.savedSuccessId);
          this.goToList();
        })
        .catch(() => {
          console.info("contact support:", editMode ? "#PLATFORM-002" : "#PLATFORM-001");
        })
        .finally(finallyBlock);
    },

    confirmRemovePlatform() {
      this.removeLoading = true;
      this.noActionBtn = true;

      const styleInline = 'style="text-wrap: pretty;"';
      const message = `
        <h2 class="text-2xl mb-5 font-bold" ${styleInline}>${this.$t(`${this.viewRoot}.message_delete_confirm_1`)}</h2>
        <p ${styleInline}>${this.$t(`${this.viewRoot}.message_delete_confirm_2`)}</p>
      `;
      this.$refs.alertMessage.show(
        message,
        true,
        true,
        this.removePlatform,
        true,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        this.cancelWarning
      );
    },

    cancelWarning() {
      this.removeLoading = false;
      this.noActionBtn = false;
    },

    removePlatform() {
      if (!this.platform.id) {
        this.$refs.alertMessage.hide();
        return;
      }

      ScienceSourceService.remove(this.platform.id)
        .then(() => {
          this.showToast(this.messages.removedSuccessId);
          this.$refs.alertMessage.hide();
          this.goToList();
        })
        .catch(() => {
          console.info("contact support: #PLATFORM-003");
        })
        .finally(this.cancelWarning);
    },

    goToList() {
      this.$router.push({ path: "/science-source-platforms-list" });
    },

    showToast(id) {
      Toastify({
        node: cash(`#${id}`).clone().removeClass("hidden")[0],
        duration: 3000,
        gravity: "top",
        position: "right",
        close: true,
      }).showToast();
    },
  },
};
</script>

<style lang="scss" scoped>
.no__action__btn {
  pointer-events: none;
  opacity: 0.5;

  &.ignore__opacity {
    opacity: 1;
  }
}

.wrapper__loading {
  width: 20%;
  transform: translateY(100%);
}
</style>
